import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchCheckLogin,
  fetchLogin,
  fetchRegister,
  fetchForgotPassword,
} from "./authAPI";
import { getBrowserInfo } from "helpers/browserDetection";

const initialState = {
  value: null,
  status: "idle",
  error: "",
  tosRegister: false,
  status_login: false,
  twoFactorEnabled: false,
  browserInfo: null,
  userid: "",
  data_login: "",
};

export const checkLogin = createAsyncThunk("auth/checkLogin", async () => {
  const response = await fetchCheckLogin();
  return response;
});

export const userLogin = createAsyncThunk(
  "auth/login",
  async (loginCredential) => {
    const response = await fetchLogin(loginCredential);
    return response;
  }
);

export const userForgotPassword = createAsyncThunk(
  "auth/forgot-password",
  async (email) => {
    const response = await fetchForgotPassword(email);
    return response;
  }
);

export const userRegister = createAsyncThunk(
  "auth/register",
  async (registerCredential) => {
    const response = await fetchRegister(registerCredential);
    return response;
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setTosRegister: (state, action) => {
      state.tosRegister = action.payload;
    },
    resetStatus: (state, action) => {
      state.status = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userLogin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userLogin.fulfilled, (state, action) => {
        // Handle new API response format from loginAccountReact.php
        if (!action.payload.status || action.payload.error) {
          state.status = "error";
          
          // Handle different error structures
          if (action.payload.error && typeof action.payload.error === 'string') {
            state.error = action.payload.error;
          } else if (action.payload.message) {
            state.error = action.payload.message;
          } else {
            state.error = "Login gagal. Email atau password salah.";
          }
          return;
        }

        state.status = "idle";

        // Get browser info
        state.browserInfo = getBrowserInfo();

        // Handle login response
        if (action.payload.data && action.payload.data.login_method === 'iframe_dologin') {
          // For iframe login, we need to wait for the iframe to complete
          // The actual login success will be handled by the iframe monitoring
          state.twoFactorEnabled = false; // Not 2FA, but iframe login
          state.data_login = action.payload.data;
        } else if (action.payload.data && action.payload.data.login_method === 'curl_dologin') {
          // For cURL login, login is already successful
          state.twoFactorEnabled = false;
          state.status_login = true;
          state.data_login = action.payload.data;
        } else if (action.payload.data && action.payload.data.result === "twoFactorEnabled") {
          state.twoFactorEnabled = true;
        } else if (action.payload.status === true && action.payload.data) {
          // Direct login success
          state.status_login = true;
          state.data_login = action.payload.data;
        }
      })
      .addCase(userLogin.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(userRegister.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userRegister.fulfilled, (state, action) => {
        if (action.payload.error) {
          state.status = "error";
          state.error = action.payload.error.message;
          return;
        }
        state.status = "idle";
        state.redirectUrl = action.payload.data.redirect_url;
      })
      .addCase(userRegister.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(userForgotPassword.pending, (state) => {
        state.status = "loading";
      })
      .addCase(userForgotPassword.fulfilled, (state, action) => {
        if (action.payload.error || action.payload.success === false) {
          state.status = "error";
          
          // Handle different error structures from resetPasswordAccount.php
          if (action.payload.error && typeof action.payload.error === 'string') {
            state.error = action.payload.error;
          } else if (action.payload.message) {
            state.error = action.payload.message;
          } else if (action.payload.error && action.payload.error.message) {
            state.error = action.payload.error.message;
          } else {
            state.error = "Gagal mengirim link reset password";
          }
        } else {
          state.status = "fulfilled";
          state.error = "";
        }
      })
      .addCase(userForgotPassword.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message;
      })
      .addCase(checkLogin.pending, (state) => {
        state.status = "loading";
      })
      .addCase(checkLogin.fulfilled, (state, action) => {
        state.status = "idle";
        
        // Handle new whoami.php response format
        if (action.payload.status === true && action.payload.data) {
          state.data_login = action.payload.data;
          state.status_login = true;
          state.userid = action.payload.data.user ? action.payload.data.user.id : null;
        } else {
          state.status_login = false;
          state.data_login = "";
          state.userid = "";
        }
      })
      .addCase(checkLogin.rejected, (state, action) => {
        state.status = "error";
        state.error = action.error.message;
      });
  },
});

export default authSlice.reducer;
export const { setTosRegister, resetStatus } = authSlice.actions;
